import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: ""
  });

  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(form.email.trim().toLowerCase(), form.password);
      toast.success("Connexion réussie");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md animate-fadeIn">
        <div className="text-center mb-8">
          <img 
            src="/logo-studio-raneem.png" 
            alt="Studio Raneem" 
            className="h-14 w-auto mx-auto mb-4"
            style={{ filter: 'brightness(0)' }}
          />
          <p className="text-slate-500 text-sm">
            Facturation & Gestion
          </p>
        </div>

        <Card className="bg-white border-slate-200 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-xl text-center text-slate-900">
              Connexion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  className="input-field"
                  placeholder="email@example.com"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700">
                    Mot de passe
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-[#26788A] hover:text-[#1D5E6C] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    data-testid="login-password-input"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="input-field pr-10"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                data-testid="login-submit-btn"
                className="w-full btn-primary h-11"
                disabled={loading}
              >
                {loading ? "Chargement..." : "Se connecter"}
              </Button>
            </form>
            
            {/* Public registration is disabled - users are created by admins only */}
            <p className="mt-6 text-center text-slate-400 text-xs">
              Accès réservé aux membres de l'équipe
            </p>
          </CardContent>
        </Card>
        
        <p className="text-center text-slate-400 text-xs mt-6">
          © {new Date().getFullYear()} Studio Raneem - SASU
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
